import type { PublicConfig, UploadRole } from '../../../shared/types';
import { abortUpload, completeUpload, createUpload, listParts, signParts, UserFacingError } from './api';
import { toFriendlyMessage, retryExhaustedMessage } from './errors';
import { loadSession, makeFingerprint, removeSession, saveSession } from './storage';
import { SpeedTracker } from './speed';
import type { StoredPart, StoredUploadSession, UploadStatus, UploadTaskSnapshot } from './types';

interface InFlightPart {
  xhr?: XMLHttpRequest;
  loadedBytes: number;
}

interface CachedUrl {
  url: string;
  fetchedAt: number;
}

const MAX_BACKOFF_MS = 30_000;

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function backoffDelay(attempt: number, baseMs: number): number {
  const exp = Math.min(MAX_BACKOFF_MS, baseMs * 2 ** (attempt - 1));
  return exp + Math.random() * Math.min(250, exp * 0.2);
}

let nextId = 0;

export class UploadTask {
  readonly id = `task-${++nextId}-${Date.now()}`;
  readonly file: File;
  readonly fingerprint: string;

  private message?: string;
  private groupId?: string;
  private role?: UploadRole;
  private config: PublicConfig;
  private onUpdate: (snapshot: UploadTaskSnapshot) => void;

  private status: UploadStatus = 'waiting';
  private errorMessage?: string;

  private sessionId?: string;
  private key?: string;
  private uploadId?: string;
  private filename: string;
  private fileSize: number;
  private partSize = 0;
  private totalParts = 0;
  private startedAt?: number;
  private completedAt?: number;
  private finalSize?: number;

  private completedParts = new Map<number, { etag: string; size: number }>();
  private pendingParts: number[] = [];
  private inFlight = new Map<number, InFlightPart>();
  private signedUrlCache = new Map<number, CachedUrl>();
  private signingPromise: Promise<void> | null = null;

  private speedTracker = new SpeedTracker();

  constructor(
    file: File,
    opts: {
      message?: string;
      groupId?: string;
      role?: UploadRole;
      config: PublicConfig;
      onUpdate: (snapshot: UploadTaskSnapshot) => void;
    },
  ) {
    this.file = file;
    this.filename = file.name;
    this.fileSize = file.size;
    this.message = opts.message;
    this.groupId = opts.groupId;
    this.role = opts.role;
    this.config = opts.config;
    this.onUpdate = opts.onUpdate;
    this.fingerprint = makeFingerprint(file.name, file.size, file.lastModified);
  }

  getRole(): UploadRole | undefined {
    return this.role;
  }

  getSnapshot(): UploadTaskSnapshot {
    const uploadedBytes = this.calculateUploadedBytes();
    const totalBytes = this.fileSize;
    const percent = totalBytes > 0 ? Math.min(100, (uploadedBytes / totalBytes) * 100) : 0;
    return {
      id: this.id,
      filename: this.filename,
      fileSize: this.status === 'completed' && this.finalSize !== undefined ? this.finalSize : this.fileSize,
      status: this.status,
      errorMessage: this.errorMessage,
      completedAt: this.completedAt,
      durationSeconds:
        this.completedAt && this.startedAt ? (this.completedAt - this.startedAt) / 1000 : undefined,
      progress: {
        uploadedBytes,
        totalBytes,
        percent,
        speedBytesPerSecond: this.speedTracker.getSpeedBytesPerSecond(),
        etaSeconds: this.speedTracker.getEtaSeconds(Math.max(0, totalBytes - uploadedBytes)),
      },
    };
  }

  private calculateUploadedBytes(): number {
    let bytes = 0;
    for (const part of this.completedParts.values()) bytes += part.size;
    for (const entry of this.inFlight.values()) bytes += entry.loadedBytes;
    return Math.min(bytes, this.fileSize);
  }

  private emit(): void {
    this.onUpdate(this.getSnapshot());
  }

  private emitProgress(): void {
    this.speedTracker.update(this.calculateUploadedBytes());
    this.emit();
  }

  private fail(message: string): void {
    this.status = 'failed';
    this.errorMessage = message;
    for (const entry of this.inFlight.values()) entry.xhr?.abort();
    this.inFlight.clear();
    this.emit();
  }

  private persist(): void {
    if (!this.sessionId) return;
    const stored: StoredUploadSession = {
      fingerprint: this.fingerprint,
      sessionId: this.sessionId,
      key: this.key ?? '',
      uploadId: this.uploadId ?? '',
      filename: this.file.name,
      fileSize: this.fileSize,
      lastModified: this.file.lastModified,
      partSize: this.partSize,
      totalParts: this.totalParts,
      completedParts: [...this.completedParts.entries()].map(
        ([partNumber, p]): StoredPart => ({ partNumber, etag: p.etag, size: p.size }),
      ),
      message: this.message,
      savedAt: Date.now(),
      startedAt: this.startedAt,
    };
    saveSession(stored);
  }

  // -- lifecycle -----------------------------------------------------------

  async start(): Promise<void> {
    if (this.status === 'completed' || this.status === 'completing') return;
    this.errorMessage = undefined;
    this.status = 'preparing';
    this.emit();

    try {
      if (!this.sessionId) {
        const stored = loadSession(this.fingerprint);
        if (stored) {
          this.sessionId = stored.sessionId;
          this.partSize = stored.partSize;
          this.totalParts = stored.totalParts;
          this.startedAt = stored.startedAt;
          this.completedParts = new Map(stored.completedParts.map((p) => [p.partNumber, { etag: p.etag, size: p.size }]));
          await this.verifyWithServer();
        } else {
          await this.createFresh();
        }
      } else {
        await this.verifyWithServer();
      }
    } catch (err) {
      this.fail(toFriendlyMessage(err));
      return;
    }

    if (this.status !== 'preparing') return; // cancelled while preparing

    this.pendingParts = [];
    for (let n = 1; n <= this.totalParts; n++) {
      if (!this.completedParts.has(n)) this.pendingParts.push(n);
    }

    if (this.pendingParts.length === 0) {
      await this.finish();
      return;
    }

    this.status = 'uploading';
    this.emit();
    this.scheduleUploads();
  }

  private async createFresh(): Promise<void> {
    const res = await createUpload({
      filename: this.file.name,
      fileSize: this.file.size,
      contentType: this.file.type || undefined,
      lastModified: this.file.lastModified,
      message: this.message,
      groupId: this.groupId,
      role: this.role,
    });
    this.sessionId = res.sessionId;
    this.key = res.key;
    this.uploadId = res.uploadId;
    this.partSize = res.partSize;
    this.totalParts = res.totalParts;
    this.completedParts = new Map();
    this.startedAt = Date.now();
    this.persist();
  }

  private async verifyWithServer(): Promise<void> {
    try {
      const result = await listParts(this.sessionId!);
      this.filename = result.filename || this.filename;
      this.fileSize = result.fileSize;
      this.partSize = result.partSize;
      this.totalParts = result.totalParts;

      // The server may have completed (or aborted) this session on a
      // previous attempt whose response we never received (e.g. the tab
      // closed right after the request landed). Recognize that instead of
      // re-driving a multipart upload whose uploadId R2 has already
      // finalized or invalidated.
      if (result.status === 'completed') {
        this.finalSize = result.fileSize;
        this.status = 'completed';
        this.completedAt = Date.now();
        removeSession(this.fingerprint);
        this.emit();
        return;
      }
      if (result.status === 'aborted') {
        removeSession(this.fingerprint);
        this.sessionId = undefined;
        await this.createFresh();
        return;
      }

      // Authoritative: what R2 actually received overrides local guesses.
      this.completedParts = new Map(result.parts.map((p) => [p.partNumber, { etag: p.etag, size: p.size }]));
      if (!this.startedAt) this.startedAt = Date.now();
      this.persist();
    } catch (err) {
      if (err instanceof UserFacingError && err.code === 'session_not_found') {
        removeSession(this.fingerprint);
        this.sessionId = undefined;
        await this.createFresh();
        return;
      }
      throw err;
    }
  }

  private scheduleUploads(): void {
    if (this.status !== 'uploading') return;
    while (this.inFlight.size < this.config.uploadConcurrency && this.pendingParts.length > 0) {
      const partNumber = this.pendingParts.shift()!;
      this.inFlight.set(partNumber, { loadedBytes: 0 });
      void this.runPartUpload(partNumber, 1);
    }
    if (this.pendingParts.length === 0 && this.inFlight.size === 0) {
      void this.finish();
    }
  }

  private hasFreshUrl(partNumber: number): boolean {
    const cached = this.signedUrlCache.get(partNumber);
    if (!cached) return false;
    const maxAgeMs = Math.max(30, this.config.presignedUrlExpirySeconds - 60) * 1000;
    return Date.now() - cached.fetchedAt < maxAgeMs;
  }

  private async ensureSignedUrls(primary: number): Promise<string> {
    if (!this.hasFreshUrl(primary)) {
      const lookahead = [primary, ...this.pendingParts].filter((p) => !this.hasFreshUrl(p));
      const batch = lookahead.slice(0, Math.max(this.config.uploadConcurrency * 2, 4));

      if (this.signingPromise) {
        await this.signingPromise;
      }
      if (!this.hasFreshUrl(primary)) {
        this.signingPromise = signParts(this.sessionId!, batch).then((res) => {
          const now = Date.now();
          for (const p of res.parts) this.signedUrlCache.set(p.partNumber, { url: p.url, fetchedAt: now });
        });
        try {
          await this.signingPromise;
        } finally {
          this.signingPromise = null;
        }
      }
    }
    const cached = this.signedUrlCache.get(primary);
    if (!cached) throw new Error('Failed to obtain an upload URL for this part.');
    return cached.url;
  }

  private async runPartUpload(partNumber: number, attempt: number): Promise<void> {
    try {
      const url = await this.ensureSignedUrls(partNumber);

      // The task may have been paused/cancelled while we were awaiting a
      // signed URL; bail out before issuing an actual HTTP request.
      if (this.status !== 'uploading' && this.status !== 'retrying') {
        throw Object.assign(new Error('aborted'), { aborted: true });
      }

      const start = (partNumber - 1) * this.partSize;
      const end = Math.min(start + this.partSize, this.fileSize);
      const blob = this.file.slice(start, end);

      const etag = await this.putPart(url, blob, partNumber);

      this.inFlight.delete(partNumber);
      this.completedParts.set(partNumber, { etag, size: blob.size });
      this.persist();
      this.emitProgress();
      this.scheduleUploads();
    } catch (err: any) {
      this.inFlight.delete(partNumber);

      if (err?.aborted) {
        // Intentional pause/cancel — the caller is responsible for requeueing.
        return;
      }

      if (this.status !== 'uploading' && this.status !== 'retrying') return;

      if (attempt >= this.config.maxRetries) {
        this.fail(retryExhaustedMessage());
        return;
      }

      this.status = 'retrying';
      this.emit();

      this.signedUrlCache.delete(partNumber);
      await sleep(backoffDelay(attempt, this.config.retryBaseDelayMs));

      if (this.status !== 'retrying' && this.status !== 'uploading') {
        // Paused or cancelled while waiting to retry: give the part back to
        // the pending queue instead of dropping it silently.
        if (this.status === 'paused') this.pendingParts.unshift(partNumber);
        return;
      }
      this.status = 'uploading';
      this.inFlight.set(partNumber, { loadedBytes: 0 });
      void this.runPartUpload(partNumber, attempt + 1);
    }
  }

  private putPart(url: string, blob: Blob, partNumber: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      const entry = this.inFlight.get(partNumber);
      if (entry) entry.xhr = xhr;

      xhr.open('PUT', url, true);
      xhr.upload.onprogress = (e) => {
        if (!e.lengthComputable) return;
        const inflightEntry = this.inFlight.get(partNumber);
        if (inflightEntry) inflightEntry.loadedBytes = e.loaded;
        this.emitProgress();
      };
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          const etag = xhr.getResponseHeader('ETag');
          if (!etag) {
            reject(new Error('Upload succeeded but no ETag was returned.'));
            return;
          }
          resolve(etag);
        } else {
          reject(new Error(`Chunk upload failed (HTTP ${xhr.status}).`));
        }
      };
      xhr.onerror = () => reject(new Error('A network error interrupted this chunk upload.'));
      xhr.ontimeout = () => reject(new Error('Chunk upload timed out.'));
      xhr.onabort = () => reject(Object.assign(new Error('aborted'), { aborted: true }));
      xhr.send(blob);
    });
  }

  private async finish(): Promise<void> {
    this.status = 'completing';
    this.emit();
    try {
      const parts = [...this.completedParts.entries()].map(([partNumber, p]) => ({ partNumber, etag: p.etag }));
      const res = await completeUpload(this.sessionId!, { parts });
      removeSession(this.fingerprint);
      this.status = 'completed';
      this.completedAt = Date.now();
      this.finalSize = res.size;
      this.emit();
    } catch (err) {
      this.fail(toFriendlyMessage(err));
    }
  }

  // -- user controls ---------------------------------------------------------

  pause(): void {
    if (this.status !== 'uploading' && this.status !== 'retrying') return;
    this.status = 'paused';
    for (const [partNumber, entry] of this.inFlight) {
      entry.xhr?.abort();
      this.pendingParts.unshift(partNumber);
    }
    this.inFlight.clear();
    this.emit();
  }

  resume(): void {
    if (this.status !== 'paused' && this.status !== 'failed') return;
    this.errorMessage = undefined;
    if (this.status === 'failed') {
      void this.start();
      return;
    }
    this.status = 'uploading';
    this.emit();
    this.scheduleUploads();
  }

  async cancel(): Promise<void> {
    const sessionId = this.sessionId;
    this.status = 'cancelled';
    for (const entry of this.inFlight.values()) entry.xhr?.abort();
    this.inFlight.clear();
    this.pendingParts = [];
    removeSession(this.fingerprint);
    this.emit();
    if (sessionId) {
      try {
        await abortUpload(sessionId);
      } catch {
        // Best-effort cleanup; the server also sweeps abandoned sessions.
      }
    }
  }

  isActive(): boolean {
    return this.status === 'uploading' || this.status === 'retrying' || this.status === 'preparing' || this.status === 'completing';
  }
}
